import { notification } from 'antd';
import { PaperClipOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { InboundEmail, InboundEmailAttachment, ListEmailsRequest } from '../api/models';
import APIClient from '../api/api';
import { debounce } from 'lodash';
import { formatDateTime } from '../utils';
import { Column, DataTable, Pagination, StatusTag } from './ui';
import { PageFooter, PageHead, PageToolbar } from '../layout';
import EmailDetailModal from './EmailDetailModal';

export interface Props {
  filterTag?: string;
}

const PAGE_SIZE = 25;

const Emails = (props: Props) => {
  const [emails, setEmails] = useState<InboundEmail[]>();
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedEmailId = searchParams.get('email') ? Number(searchParams.get('email')) : null;

  function setSelectedEmailId(id: number | null) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id != null) next.set('email', String(id));
      else next.delete('email');
      return next;
    }, { replace: true });
  }

  useEffect(() => {
    loadEmails(1, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filterTag]);

  useEffect(() => {
    loadEmails(1, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  async function loadEmails(p: number, q: string) {
    setLoading(true);
    try {
      const req: ListEmailsRequest = {};
      req.tag = props.filterTag;
      req.search = q || undefined;
      const resp = await APIClient.listEmails({ page: p, pageSize: PAGE_SIZE }, req);
      setEmails(resp.content);
      setTotal(resp.total);
      setPage(resp.currentPage || p);
    } catch (e) {
      notification.error({ message: 'Failed to list emails at this time.' });
    } finally {
      setLoading(false);
    }
  }

  const debouncedSearch = useMemo(() => debounce((value: string) => setSearch(value), 400), []);

  const selectedIndex = selectedEmailId != null && emails
    ? emails.findIndex((e) => e.id === selectedEmailId)
    : -1;

  const prevEmail = useCallback(() => {
    if (!emails || selectedIndex <= 0) return;
    setSelectedEmailId(emails[selectedIndex - 1].id);
  }, [emails, selectedIndex]);

  const nextEmail = useCallback(() => {
    if (!emails || selectedIndex < 0 || selectedIndex >= emails.length - 1) return;
    setSelectedEmailId(emails[selectedIndex + 1].id);
  }, [emails, selectedIndex]);

  const columns: Column<InboundEmail>[] = [
    {
      key: 'sentAt',
      title: 'Received',
      width: 170,
      render: (e) => (
        <span className="font-mono text-[12px] text-text-2 tabular-nums">
          {formatDateTime(e.sentAt)}
        </span>
      ),
    },
    {
      key: 'venue',
      title: 'Venue',
      width: 180,
      render: (e) =>
        e.venue ? (
          <span className="text-text-2 text-[12.5px]">
            {e.venue}
            {e.account && <span className="text-text-3"> · {e.account}</span>}
          </span>
        ) : (
          <span className="text-text-3">—</span>
        ),
    },
    {
      key: 'subject',
      title: 'Subject',
      render: (e) => (
        <span className="text-text text-[12.5px]">
          {e.subject || <span className="text-text-3">(no subject)</span>}
        </span>
      ),
    },
    {
      key: 'tags',
      title: 'Tags',
      render: (e) => (
        <div className="flex gap-1 flex-wrap">
          {e.tags.map((tag) => (
            <StatusTag key={tag} tone="accent" dot={false}>
              {tag}
            </StatusTag>
          ))}
        </div>
      ),
    },
    {
      key: 'attachments',
      title: <PaperClipOutlined />,
      width: 200,
      render: (e) => (
        <div className="flex flex-col gap-0.5">
          {e.attachments.map((a: InboundEmailAttachment) => (
            <a
              key={a.id}
              target="_blank"
              rel="noreferrer"
              href={APIClient.getAttachmentDownloadUrl(a)}
              onClick={(ev) => ev.stopPropagation()}
              className="text-accent text-[11.5px] truncate max-w-[200px] hover:opacity-80"
            >
              {a.filename}
            </a>
          ))}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHead
        title="Emails"
        subtitle={
          props.filterTag
            ? `Inbound emails tagged \`${props.filterTag}\`.`
            : 'All inbound parsed emails across venues.'
        }
      />
      <PageToolbar>
        <input
          type="text"
          placeholder="Search emails…"
          defaultValue={search}
          onChange={(e) => debouncedSearch(e.target.value)}
          className="bg-bg-elevated border border-transparent rounded-md px-3 py-1 w-[280px] text-[12.5px] text-text outline-none focus:border-border-strong placeholder:text-text-3"
        />
        <span className="ml-auto text-[11px] text-text-3 font-mono">
          {emails ? (
            <>
              total <b className="text-text-2">{total.toLocaleString()}</b>
            </>
          ) : (
            'loading…'
          )}
        </span>
      </PageToolbar>

      <DataTable
        columns={columns}
        data={emails ?? []}
        rowKey={(e) => String(e.id)}
        loading={loading}
        emptyMessage="No emails found."
        onRowClick={(e) => setSelectedEmailId(e.id)}
        rowHref={(e) => `/emails/${e.id}`}
      />
      <PageFooter
        left={
          <span>
            Showing <b className="text-text-2">{emails?.length ?? 0}</b> of{' '}
            <b className="text-text-2">{total.toLocaleString()}</b>
          </span>
        }
        right={
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={(p) => loadEmails(p, search)}
            loading={loading}
          />
        }
      />

      {selectedEmailId != null && (
        <EmailDetailModal
          emailId={selectedEmailId}
          onClose={() => setSelectedEmailId(null)}
          onTagsChanged={() => loadEmails(page, search)}
          onPrev={selectedIndex > 0 ? prevEmail : undefined}
          onNext={emails && selectedIndex >= 0 && selectedIndex < emails.length - 1 ? nextEmail : undefined}
        />
      )}
    </>
  );
};

export default Emails;
